import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api, formatApiError } from '@/App';
import { Lightning, Lock, CheckCircle } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/217o5tly_Marema_Logo.jpg";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      await api.post('/auth/reset-password', null, { params: { token, new_password: password } });
      setIsSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#0A192F] mb-4">Invalid Reset Link</h2>
          <p className="text-[#4B5563] mb-6">This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password">
            <Button className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-none px-6 py-3">
              Request New Link
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Marema Consulting" className="h-[4.5rem] w-auto" />
          </Link>
        </div>

        <div className="bg-white border border-[#E5E7EB] p-8 shadow-sm">
          {isSuccess ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-[#10B981]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[#10B981]" weight="fill" />
              </div>
              <h2 className="text-2xl font-bold text-[#0A192F] tracking-tight mb-2">Password Reset!</h2>
              <p className="text-[#4B5563] mb-6">
                Your password has been successfully reset. You can now sign in with your new password.
              </p>
              <Link to="/login">
                <Button className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-none px-6 py-3 font-medium">
                  Sign In
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight mb-2">Reset Password</h2>
                <p className="text-[#4B5563]">Enter your new password</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
                <div>
                  <Label htmlFor="password" className="text-sm font-semibold text-[#0A192F] mb-2 block">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4B5563]" />
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="Min. 8 characters"
                      className="pl-10 border-[#E5E7EB] rounded-none bg-[#F9FAFB] focus:ring-[#0055FF] focus:border-[#0055FF] h-12"
                      data-testid="password-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-sm font-semibold text-[#0A192F] mb-2 block">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4B5563]" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      placeholder="Confirm your new password"
                      className="pl-10 border-[#E5E7EB] rounded-none bg-[#F9FAFB] focus:ring-[#0055FF] focus:border-[#0055FF] h-12"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-none h-12 font-medium transition-transform hover:-translate-y-0.5"
                  data-testid="submit-btn"
                >
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
